import { AddCircle, Check, Edit, RemoveCircle } from '@mui/icons-material';
import { Box, Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUpdateChannelGuide } from '~/api/channel-guides';
import { Tooltip } from '~/components/Tooltip';
import { ActionButtonLink } from '~/components/button';
import { Count } from '~/components/count';
import { ScrollableDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { SearchBar } from '~/components/forms/search-bar';
import { SearchContainer } from '~/components/search';
import { TableAction } from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import CreateChannelGuideIllustration from '~/images/illustrations/create-channel-guide.svg';
import { searchRegex } from '~/lib/search';
import { formatEncryption } from '~/pages/channels/lib/formatters';
import { AddChannelsDialog } from '../../components/AddChannelsDialog';
import type {
  ChannelGuideSettings__Channel as Channel,
  ChannelGuideSettings__ChannelGuide as ChannelGuide,
} from '../../queries/settings.generated';

export interface ChannelsProps {
  channelGuide: ChannelGuide;
  channels: readonly Channel[];
}

export const Channels = ({ channelGuide, channels }: ChannelsProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const theme = useTheme();
  const navigate = useNavigate();

  const [addChannelsDialogOpen, setAddChannelsDialogOpen] = useState(false);

  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isXlAndDown = useMediaQuery(theme.breakpoints.down('xl'));

  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const [search, setSearch] = useState('');

  const link = useLink();

  const channelGuideChannels = useMemo(() => {
    const searchReg = searchRegex(search);
    return channelGuide.channels.filter((item) => searchReg.test(item.name));
  }, [channelGuide.channels, search]);

  const [confirmRemoveChannel, confirmRemoveChannelProps] = useConfirmDialog();

  const [updateChannelGuide] = useUpdateChannelGuide();

  const removeChannel = useCallback(
    async (channel: Channel) => {
      if (!(await confirmRemoveChannel())) return;

      await updateChannelGuide({
        variables: {
          input: {
            id: channelGuide.id,
            patch: {
              channelIds: channelGuide.channels.filter((x) => x.id !== channel.id).map((x) => x.id),
            },
          },
        },
      });
    },
    [channelGuide.channels, channelGuide.id, confirmRemoveChannel, updateChannelGuide],
  );

  const columns: GridColDef<Channel>[] = useMemo(
    () => [
      {
        field: 'thumbnailFile',
        headerName: t('common.labels.thumbnail'),
        minWidth: 100,
        renderCell: ({ row: channel, value: thumbnailFile }) => {
          return thumbnailFile ? (
            <Tooltip title={`Channel ID: ${channel.id}`}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  paddingTop: theme.spacing(1),
                  paddingBottom: theme.spacing(1),
                  backgroundColor: channel.backgroundColor,
                  height: '75%',
                }}
              >
                <img
                  alt={channel.name}
                  crossOrigin="anonymous"
                  src={thumbnailFile.uri}
                  style={{ margin: '0.5rem', height: '100%' }}
                />
              </Box>
            </Tooltip>
          ) : null;
        },
        sortable: false,
      } satisfies GridColDef<Channel, Channel['thumbnailFile']>,
      {
        field: 'number',
        headerName: t('pages.channel_guides.detail.channels_section.columns.number'),
        resizable: false,
        sortable: true,
        minWidth: 100,
      },
      {
        field: 'name',
        headerName: t('common.labels.name'),
        flex: 1,
        minWidth: isSmallAndUp ? 180 : 0,
        resizable: true,
        sortable: true,
      },
      {
        field: 'url',
        minWidth: 300,
        resizable: true,
        sortable: true,
        headerName: t('pages.channel_guides.detail.channels_section.columns.url'),
      },
      {
        field: 'outputUrl',
        minWidth: 300,
        resizable: true,
        sortable: true,
        headerName: t('pages.channel_guides.detail.channels_section.columns.output_url'),
        renderCell: ({ row }) => {
          // Don't display output URL when encryption is NONE
          if (row.encryption === 'NONE') {
            return '';
          }
          return row.outputUrl || '';
        },
      },
      {
        field: 'encryption',
        headerName: t('pages.channel_guides.detail.channels_section.columns.encryption'),
        resizable: false,
        sortable: false,
        width: 110,
        valueFormatter: ({ value }) => formatEncryption(value),
      } satisfies GridColDef<Channel, Channel['encryption'], string>,
      {
        field: 'source',
        headerName: t('common.labels.source'),
        minWidth: 125,
        resizable: false,
        sortable: true,
      },
      ...(currentNetwork.settings.enableLiveChannelStreaming
        ? [
            {
              align: 'center' as const,
              field: 'enabledForLiveStreaming',
              headerName: t('pages.channel_guides.detail.channels_section.columns.live_streaming'),
              minWidth: 120,
              resizable: false,
              sortable: true,
              renderCell: ({ row }: { row: Channel }) =>
                row.enabledForLiveStreaming ? <Check color="success" /> : null,
            },
          ]
        : []),
      {
        align: 'right',
        field: 'actions',
        getActions: ({ id, row }) => {
          return [
            <ActionButtonLink
              key={id}
              title={t('common.actions.edit')}
              Icon={Edit}
              to={link(`/channels/${id}`)}
            />,
            <TableAction
              color="error"
              key={id}
              title={t('common.actions.remove')}
              Icon={RemoveCircle}
              onClick={() => removeChannel(row)}
            />,
          ];
        },
        sortable: false,
        type: 'actions',
      },
    ],
    [
      currentNetwork.settings.enableLiveChannelStreaming,
      isSmallAndUp,
      link,
      removeChannel,
      t,
      theme,
    ],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      encryption: !isLargeAndDown,
      outputUrl: !isLargeAndDown,
      source: !isXlAndDown,
      thumbnailFile: !isSmallAndDown,
      url: !isSmallAndDown,
      enabledForLiveStreaming: !isSmallAndDown,
    }));
  }, [isLargeAndDown, isSmallAndDown, isXlAndDown]);

  return (
    <>
      <Paper sx={{ mt: 2 }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            padding: theme.spacing(2),
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'column',
              gap: theme.spacing(1),
            },
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 2 }}>
            <Box>
              <Typography variant="h4">
                {t('pages.channel_guides.detail.channels_section.title')}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {t('pages.channel_guides.detail.channels_section.description')}
              </Typography>
            </Box>
          </Box>
          <Button
            color="primary"
            onClick={() => setAddChannelsDialogOpen(() => true)}
            startIcon={<AddCircle />}
            variant="contained"
          >
            {t('pages.channel_guides.detail.channels_section.add_button')}
          </Button>
        </Box>
        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            padding: theme.spacing(2),
            borderTop: '1px solid',
            borderColor: theme.palette.borders.dark,
          }}
        >
          <>
            <SearchContainer>
              <SearchBar
                onChange={setSearch}
                placeholder={t('pages.channel_guides.detail.channels_section.search_placeholder')}
                search={search}
              />
              <Count
                selectedCount={0}
                totalCount={channelGuide.channelCount}
                thing="common.pluralize.channel_plural"
              />
            </SearchContainer>
            <ScrollableDataGrid
              columns={columns}
              columnVisibilityModel={showColumns}
              disableRowSelectionOnClick
              onRowClick={({ row }: GridRowParams<Channel>) => {
                navigate(link(`/channels/${row.id}`));
              }}
              rows={channelGuideChannels}
              slots={{
                noRowsOverlay: () => {
                  return (
                    <EmptyState
                      illustration={CreateChannelGuideIllustration}
                      header={t('pages.channel_guides.detail.channels_section.empty_state.header')}
                      description={t(
                        'pages.channel_guides.detail.channels_section.empty_state.description',
                      )}
                    />
                  );
                },
              }}
            />
          </>
        </Box>
      </Paper>
      <AddChannelsDialog
        channelGuide={channelGuide}
        channels={channels}
        close={() => setAddChannelsDialogOpen(false)}
        open={addChannelsDialogOpen}
      />
      <ConfirmDialog
        {...confirmRemoveChannelProps}
        cancel={t('common.actions.cancel')}
        confirm={t('common.dialogs.remove')}
        deleteConfirm
        prompt={t('pages.channel_guides.dialogs.remove_channel.prompt')}
        title={t('pages.channel_guides.dialogs.remove_channel.title')}
      />
    </>
  );
};
